import { getCurrentUser } from '../user/current_user.js?v=20260722a';
import { ProjectPersistenceService } from '../project/project_persistence_service.js?v=20260904f';
import { openDossierModal } from '../app/lazy_dialog_runtime.js?v=20260904f';
import { renderSidebarUserCard } from './sidebar_user_card.js?v=20260806a';
import { S } from '../app/app_state.js?v=20260817c';
import { escapeHtml as escapeHtmlValue } from '../utils/html_utils.js?v=20260706d';
import { t } from '../i18n/i18n_service.js?v=20260722a';
import { renderDrawingSheetTitlePanel } from '../project/drawing_sheet_panel.js?v=20260903c';
import { renderCoreGroupLifecyclePanel } from '../project/core_group_lifecycle_panel.js?v=20260903c';
import {
  hideWorkspaceLoadingOverlay,
  showWorkspaceLoadingOverlay
} from '../ui/workspace_loading_overlay.js?v=20260826a';

const DEFAULT_PROJECT_NUMBER = '101100000';

const panels = new Map();
let projectSearchInitialFocusApplied = false;
let projectSearchInitialFocusSeq = 0;

function normalizeSidebarText(value) {
  return String(value ?? '').trim();
}

function sidebarUserDisplayName(user) {
  return normalizeSidebarText(user?.display_name || `${user?.vorname || ''} ${user?.nachname || ''}`);
}

function sidebarUserRoleCodes(user) {
  return [
    user?.rolle_code,
    user?.rolle_name,
    ...(Array.isArray(user?.roleCodes) ? user.roleCodes : []),
    ...(Array.isArray(user?.roles) ? user.roles.flatMap(role => [role?.rolle_code, role?.rolle_name]) : [])
  ].map(value => normalizeSidebarText(value).toUpperCase()).filter(Boolean);
}

function userHasSidebarRole(user, acceptedRoles) {
  const roleSet = new Set(acceptedRoles.map(role => normalizeSidebarText(role).toUpperCase()));
  return sidebarUserRoleCodes(user).some(role => roleSet.has(role));
}

function ensureCurrentUserProjectRoleDefaults() {
  const user = getCurrentUser();
  const displayName = sidebarUserDisplayName(user);
  if (!user || !displayName) return;
  if (!normalizeSidebarText(S.project?.seller) && userHasSidebarRole(user, ['VERKAUF', 'VERKAEUFER', 'VERKÄUFER', 'VERTRIEB', 'SALES'])) {
    S.project.seller = displayName;
    S.project.sellerUserId = user.benutzer_id ?? null;
  }
  if (!normalizeSidebarText(S.project?.avor) && userHasSidebarRole(user, ['AVOR', 'ARBEITSVORBEREITUNG'])) {
    S.project.avor = displayName;
    S.project.avorUserId = user.benutzer_id ?? null;
  }
}

/**
 * Escaped Text fuer globale Sidebar-Panels.
 *
 * Zweck:
 * - haelt die Core-Sidebar unabhaengig von systemeigenen UI-Helfern
 * - verhindert HTML-Injektion in projektweiten Eingabewerten
 *
 * @param {*} value - Wert aus Datei- oder Projektstate.
 * @returns {string} Sicherer HTML-Text.
 */
export function escapeHtml(value) {
  return escapeHtmlValue(value);
}

/**
 * Registriert eine Karte fuer die linke Seitenleiste.
 *
 * Zweck:
 * - trennt globale Karten von company-/systemspezifischen Erweiterungen
 * - erlaubt spaetere Sidebar-Panels, ohne Core-Refreshlogik zu duplizieren
 *
 * @param {Object} panel - Paneldefinition mit `id`, `hostId`, `scope` und `render`.
 * @returns {Object|null} Registriertes Panel oder `null` bei unvollstaendiger Definition.
 */
export function registerPanel(panel) {
  if (!panel?.id || !panel.hostId || typeof panel.render !== 'function') return null;
  const normalizedPanel = {
    scope: 'system',
    companyId: null,
    systemId: null,
    ...panel
  };
  panels.set(normalizedPanel.id, normalizedPanel);
  return normalizedPanel;
}

/**
 * Rendert eine konkrete Sidebar-Karte.
 *
 * Zweck:
 * - verwendet die registrierte Paneldefinition als einzige Quelle
 * - ersetzt Containerinhalte idempotent und verhindert dadurch Doppelungen
 *
 * @param {string} panelId - ID der registrierten Sidebar-Karte.
 * @returns {boolean} `true`, wenn ein Panel gerendert werden konnte.
 */
export function renderPanel(panelId) {
  const panel = panels.get(panelId);
  const host = panel ? document.getElementById(panel.hostId) : null;
  if (!panel || !host) return false;
  panel.render(host);
  return true;
}

/**
 * Rendert alle globalen Sidebar-Karten.
 *
 * Zweck:
 * - Datei- und Projektkarte sind bewusst `scope: "global"`
 * - sie bleiben bei Reload, Company-Wechsel und Systemwechsel immer sichtbar
 *
 * Seiteneffekte: Aktualisiert nur die DOM-Container globaler Panels.
 */
export function renderGlobalPanels() {
  panels.forEach(panel => {
    if (panel.scope === 'global') renderPanel(panel.id);
  });
}

function debounceSidebar(fn, delay = 180) {
  let timer = 0;
  return (...args) => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn(...args), delay);
  };
}

function renderLookupList(host, listName, rows, renderRow, onSelect, options = {}) {
  const list = host.querySelector(`[data-project-lookup="${listName}"]`);
  if (!list) return;
  if (!Array.isArray(rows) || !rows.length) {
    if (options.hideWhenEmpty === true) {
      hideLookupList(host, listName);
      return;
    }
    list.innerHTML = '<div class="project-lookup-empty">Keine Treffer</div>';
    list.hidden = false;
    return;
  }
  list.innerHTML = rows.map((row, index) => renderRow(row, index)).join('');
  list.hidden = false;
  list.querySelectorAll('[data-project-lookup-index]').forEach(button => {
    button.addEventListener('click', () => {
      const index = Number(button.dataset.projectLookupIndex);
      const row = rows[index];
      if (row) onSelect(row);
    });
  });
}

function hideLookupList(host, listName) {
  const list = host.querySelector(`[data-project-lookup="${listName}"]`);
  if (!list) return;
  list.hidden = true;
  list.innerHTML = '';
}

function projectSearchOrderRow(order, index) {
  const projectNumber = escapeHtml(order.projektnummer || '');
  const orderNumber = escapeHtml(order.auftragsnummer || '');
  const customer = escapeHtml(order.kunde || '');
  const description = escapeHtml(order.bezeichnung || '');
  return `
    <button type="button" class="project-lookup-row project-search-result" data-project-lookup-index="${index}">
      <span>${projectNumber || '-'}</span>
      <span>${orderNumber || '-'}</span>
      <span>${customer || '-'}</span>
      <span>${description || '-'}</span>
    </button>
  `;
}

function bindProjectInfoLookup(host) {
  const searchInput = host.querySelector('#projectSearch');
  let searchRun = 0;
  let orderSwitchInProgress = false;

  const normalizeInputValue = value => String(value ?? '').trim();

  const setProjectWarning = message => {
    const warning = host.querySelector('[data-project-warning]');
    if (!warning) return;
    warning.textContent = message || '';
    warning.hidden = !message;
  };

  const clearProjectWarning = () => setProjectWarning('');

  const currentLoadedProjectNumber = () => normalizeInputValue(S.project?.projectNumber);
  const currentLoadedOrderNumber = () => normalizeInputValue(S.project?.orderNumber);

  const sameLoadedOrder = (projectNumber, orderNumber) => (
    normalizeInputValue(projectNumber) === currentLoadedProjectNumber()
    && normalizeInputValue(orderNumber) === currentLoadedOrderNumber()
  );

  const saveCurrentOrderBeforeSwitch = async () => {
    if (!S.file?.dirty) return true;
    if (!ProjectPersistenceService.hasProjectDrawingContent()) return true;
    const result = await ProjectPersistenceService.autosaveCurrentState();
    return Boolean(result);
  };

  const loadSelectedOrder = async order => {
    const projectNumber = String(order.projektnummer || '').trim();
    const orderNumber = String(order.auftragsnummer || '').trim();
    if (!projectNumber || !orderNumber || orderSwitchInProgress) return;
    hideLookupList(host, 'projectSearch');
    clearProjectWarning();
    if (sameLoadedOrder(projectNumber, orderNumber) && ProjectPersistenceService.hasProjectDrawingContent()) return;

    const loadingDetails = [
      `Projekt ${projectNumber}`,
      `Auftrag ${orderNumber}`
    ].join(' · ');
    const loadingToken = showWorkspaceLoadingOverlay({
      label: 'Auftrag wird geladen...',
      details: loadingDetails
    });
    orderSwitchInProgress = true;
    try {
      const saved = await saveCurrentOrderBeforeSwitch();
      if (!saved) {
        setProjectWarning('Aktueller Auftrag konnte vor dem Wechsel nicht gespeichert werden.');
        return;
      }
      await ProjectPersistenceService.loadOrderByProjectOrder(projectNumber, orderNumber);
    } catch (error) {
      console.warn('Auftrag konnte nicht geladen werden', error);
      setProjectWarning(error.message || 'Auftrag konnte nicht geladen werden.');
    } finally {
      orderSwitchInProgress = false;
      hideWorkspaceLoadingOverlay(loadingToken);
    }
  };

  const selectOrder = order => {
    loadSelectedOrder(order).catch(error => {
      console.warn('Auftrag konnte nicht geladen werden', error);
    });
  };

  const searchOrders = debounceSidebar(async () => {
    const run = ++searchRun;
    const query = searchInput?.value || '';
    clearProjectWarning();
    if (!query.trim()) {
      hideLookupList(host, 'projectSearch');
      return;
    }
    try {
      const orders = await ProjectPersistenceService.searchOrders(query);
      if (run !== searchRun) return;
      renderLookupList(host, 'projectSearch', orders, projectSearchOrderRow, selectOrder, { hideWhenEmpty: true });
    } catch (error) {
      console.warn('Projektsuche fehlgeschlagen', error);
      setProjectWarning(error.message || 'Projektsuche fehlgeschlagen.');
    }
  });

  searchInput?.addEventListener('input', searchOrders);
  searchInput?.addEventListener('keydown', event => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    const first = host.querySelector('[data-project-lookup="projectSearch"] [data-project-lookup-index]');
    first?.click();
  });
  searchInput?.addEventListener('focus', () => {
    if (searchInput.value.trim()) searchOrders();
  });

  host.addEventListener('focusout', () => {
    window.setTimeout(() => {
      if (!host.contains(document.activeElement)) {
        hideLookupList(host, 'projectSearch');
      }
    }, 0);
  });
}

function focusProjectSearchOnInitialRender(host) {
  if (projectSearchInitialFocusApplied) return;
  const searchInput = host.querySelector('#projectSearch');
  if (!searchInput) return;
  const focusSeq = ++projectSearchInitialFocusSeq;
  const focusDelays = [0, 50, 150, 350, 700];
  const isEditableElement = element => {
    if (!element || element === document.body || element === document.documentElement) return false;
    if (element.isContentEditable) return true;
    return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName);
  };
  const isVisibleElement = element => Boolean(element?.getClientRects?.().length);
  const tryFocus = () => {
    if (projectSearchInitialFocusApplied || focusSeq !== projectSearchInitialFocusSeq) return;
    if (!document.body.contains(searchInput)) return;
    const active = document.activeElement;
    if (active !== searchInput && isEditableElement(active) && isVisibleElement(active)) {
      projectSearchInitialFocusApplied = true;
      return;
    }
    if (active !== searchInput) {
      searchInput.focus({ preventScroll: true });
      searchInput.select();
    }
    if (document.activeElement === searchInput) {
      projectSearchInitialFocusApplied = true;
    }
  };
  focusDelays.forEach(delay => {
    window.setTimeout(() => {
      window.requestAnimationFrame(tryFocus);
    }, delay);
  });
}

function projectInfoDetailsHtml() {
  const projectNumber = S.project?.projectNumber || DEFAULT_PROJECT_NUMBER;
  const orderNumber = S.project?.orderNumber || '';
  const customerName = S.project?.customerName || '';
  const customerContactName = [
    S.project?.customerContactFirstName,
    S.project?.customerContactLastName
  ].map(value => String(value || '').trim()).filter(Boolean).join(' ')
    || String(S.project?.customerContactName || '').trim();
  const orderDescription = S.project?.orderDescription || '';
  const seller = S.project?.seller || '';
  const avor = S.project?.avor || '';
  return `
    <div class="project-info-details" aria-label="Aktuelle Projekt- und Auftragsangaben">
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.projektnummer', 'Projektnummer'))}</span><strong>${escapeHtml(projectNumber)}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.auftragsnummer', 'Auftragsnummer'))}</span><strong>${escapeHtml(orderNumber || '-')}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.kunde', 'Kunde'))}</span><strong>${escapeHtml(customerName || '-')}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.ansprechperson', 'Ansprechperson'))}</span><strong>${escapeHtml(customerContactName || '-')}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.bezeichnung', 'Bezeichnung'))}</span><strong>${escapeHtml(orderDescription || '-')}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.verkauf', 'Verkauf'))}</span><strong>${escapeHtml(seller || '-')}</strong></div>
      <div><span>${escapeHtml(t('sidebar_links.projekt.label.avor', 'AVOR'))}</span><strong>${escapeHtml(avor || '-')}</strong></div>
    </div>
  `;
}

export function registerCoreSidebarPanels() {
  registerPanel({
    id: 'currentUser',
    scope: 'global',
    hostId: 'sidebarUserCardPanel',
    render: renderSidebarUserCard
  });

  registerPanel({
    id: 'drawingSheetTitle',
    scope: 'global',
    hostId: 'drawingSheetTitlePanel',
    render: renderDrawingSheetTitlePanel
  });

  registerPanel({
    id: 'coreGroupLifecycle',
    scope: 'global',
    hostId: 'coreGroupLifecyclePanel',
    render: renderCoreGroupLifecyclePanel
  });

  registerPanel({
    id: 'projectInfo',
    scope: 'global',
    hostId: 'projectInfoPanel',
    render(host) {
      ProjectPersistenceService.initProjectPersistence();
      ensureCurrentUserProjectRoleDefaults();
      host.innerHTML = `
        <div class="card">
          <div class="card-title">${escapeHtml(t('sidebar_links.projekt.titel.karte', 'Projekt'))}</div>
          <div class="project-search-block">
            <label class="project-lookup-field">${escapeHtml(t('sidebar_links.projekt.label.suche', 'Suche'))}<input id="projectSearch" type="text" autocomplete="off"><div class="project-lookup-list project-search-list" data-project-lookup="projectSearch" hidden></div></label>
          </div>
          ${projectInfoDetailsHtml()}
          <div class="project-info-warning" data-project-warning hidden></div>
          <div class="project-dossier-actions">
            <button id="btnProjectDossier" class="btn" type="button">${escapeHtml(t('sidebar_links.projekt.button.dossier', 'Dossier'))}</button>
          </div>
        </div>
      `;

      bindProjectInfoLookup(host);
      focusProjectSearchOnInitialRender(host);
      host.querySelector('#btnProjectDossier')?.addEventListener('click', event => {
        const button = event.currentTarget;
        const warning = host.querySelector('[data-project-warning]');
        button.disabled = true;
        openDossierModal()
          .catch(error => {
            console.error('Dossier konnte nicht geladen werden', error);
            if (warning) {
              warning.textContent = `Dossier konnte nicht geladen werden: ${error?.message || error}`;
              warning.hidden = false;
            }
          })
          .finally(() => {
            if (button.isConnected) button.disabled = false;
          });
      });
    }
  });
}

export function renderProjectInfoPanel() {
  return renderPanel('projectInfo');
}
